import type { ProfilePoint } from './articulation';
import { ROOF, roofHeight } from './tongueTargets';

/**
 * The roof of the mouth as a surface (the 3D model's palate), not only its midline: at each depth
 * the vault spans from the gums inside the upper teeth on one side to the other, peaking at the
 * measured midline (ROOF). The arch is measured from the model's own upper teeth (BodyParts3D,
 * public/articulator/mouth.glb, 2026-10-12): the innermost |x| of the crowns at each depth and the
 * crowns' lowest point; the gum line sits a crown's height above it (about 9 mm at the incisors,
 * 7 mm at the molars: estimated). Behind the last molar the soft palate narrows toward the uvula
 * (estimated).
 */

/** [z, half width at the gums, crown's lowest y] in mm, front to back (measured). */
const ARCH: readonly (readonly [number, number, number])[] = [
  [-6, 6, -0.5],
  [-10, 9.8, 0.8],
  [-14, 14.6, 2.4],
  [-18, 16.1, 2.3],
  [-22, 16.5, 3.5],
  [-26, 19.1, 3.7],
  [-30, 19.2, 5.5],
  [-34, 19.1, 6.2],
  [-38, 20.3, 7.3],
  [-42, 19.9, 9.1],
  [-46, 20.3, 10.2],
];

/** Crown height (mm) from the incisors to the molars: the gum line above the crown's low point. */
const crownHeight = (z: number) => 9 - (2 * Math.min(1, Math.max(0, -z - 6))) / 40;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** The palate's cross-section at depth z: half width and the height of its edges (mm). */
export function palateSection(z: number): { halfWidth: number; edgeY: number } | undefined {
  const roof = roofHeight(z);
  if (roof === undefined) return undefined;
  const first = ARCH[0];
  const last = ARCH[ARCH.length - 1];
  if (!first || !last) return undefined;
  if (z >= first[0]) return { halfWidth: first[1], edgeY: first[2] + crownHeight(z) };
  for (let i = 0; i < ARCH.length - 1; i++) {
    const a = ARCH[i];
    const b = ARCH[i + 1];
    if (!a || !b || z > a[0] || z < b[0]) continue;
    const t = (z - a[0]) / (b[0] - a[0]);
    return { halfWidth: lerp(a[1], b[1], t), edgeY: lerp(a[2], b[2], t) + crownHeight(z) };
  }
  // The soft palate behind the teeth: narrowing, its edges following the roof down.
  const behind = Math.min(1, (last[0] - z) / 20);
  return {
    halfWidth: lerp(last[1], 13, behind),
    edgeY: roof - lerp(roof - last[2] - 7, 5, behind),
  };
}

/** The vault's height at x across a section: high in the middle, curving down to the gums. */
export function palateHeight(z: number, x: number): number | undefined {
  const section = palateSection(z);
  const roof = roofHeight(z);
  if (!section || roof === undefined) return undefined;
  const u = Math.min(1, Math.abs(x) / section.halfWidth);
  return section.edgeY + (roof - section.edgeY) * Math.cos((Math.PI / 2) * u) ** 0.7;
}

/** Depths the palate surface spans (mm): from just behind the incisors to the soft palate's end. */
export const PALATE_SPAN: readonly [number, number] = [-6, Math.min(...ROOF.map(([z]) => z))];

/**
 * The palate as a grid: `rows` depths by `columns` points across, [x, y, z] mm per point, front to
 * back and right to left. Indices: row * columns + column.
 */
export function palateGrid(
  rows = 40,
  columns = 17,
): { points: Float32Array; rows: number; columns: number } {
  const points = new Float32Array(rows * columns * 3);
  const [front, back] = PALATE_SPAN;
  for (let r = 0; r < rows; r++) {
    const z = lerp(front, back + 0.01, r / (rows - 1));
    const section = palateSection(z);
    const half = section?.halfWidth ?? 10;
    for (let c = 0; c < columns; c++) {
      const x = lerp(-half, half, c / (columns - 1));
      const y = palateHeight(z, x) ?? 0;
      points.set([x, y, z], (r * columns + c) * 3);
    }
  }
  return { points, rows, columns };
}

export type { ProfilePoint };
